import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Home, Calendar, Users, MessageCircle, User, Trophy, Megaphone } from 'lucide-react';
import axios from 'axios';
import { getAvatarUrl } from '../utils/avatarUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Header = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchUnreadCount();
    // Poll every 30 seconds for new messages
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications/unread-count`, {
        withCredentials: true
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      // Silently fail
    }
  };

  const navItems = [
    { path: '/dashboard', label: 'Home', icon: Home },
    { path: '/matches', label: 'Partite', icon: Calendar },
    { path: '/bacheca', label: 'Bacheca', icon: Megaphone },
    { path: '/ranking', label: 'Classifica', icon: Trophy },
    { path: '/chat', label: 'Chat', icon: MessageCircle },
    { path: '/profile', label: 'Profilo', icon: User },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const isDashboard = location.pathname === '/dashboard';

  return (
    <>
      {/* Desktop Header */}
      <header className={`z-50 hidden sm:block ${
        isDashboard
          ? 'absolute top-0 left-0 right-0 bg-transparent'
          : 'sticky top-0 backdrop-blur-xl bg-white/80 border-b border-slate-100/50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className={`${isDashboard ? 'w-10 h-10 rounded-full ring-2 ring-white/30 shadow-lg' : 'h-12 w-auto'} overflow-hidden flex-shrink-0`}>
                <img 
                  src="/logo-header.png" 
                  alt="Ananda Sport" 
                  className={`${isDashboard ? 'w-full h-full object-cover rounded-full' : 'h-12 w-auto'}`}
                />
              </div>
              <span className={`font-outfit text-lg font-semibold ${isDashboard ? 'bg-gradient-to-r from-green-300 via-white to-red-300 bg-clip-text text-transparent' : 'text-slate-900'}`}>
                Ananda Sporting Club
              </span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const showBadge = item.path === '/chat' && unreadCount > 0;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      isActive(item.path)
                        ? isDashboard ? 'bg-white/20 text-white' : 'bg-sky-100 text-sky-600'
                        : isDashboard ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                    {showBadge && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-semibold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* User */}
            <div className="flex items-center gap-3">
              <Link
                to="/profile"
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
                  isDashboard ? 'hover:bg-white/10' : 'hover:bg-slate-100'
                }`}
              >
                <div className="w-8 h-8 bg-sky-400 rounded-full flex items-center justify-center text-white text-sm font-medium overflow-hidden">
                  {getAvatarUrl(user) ? (
                    <img src={getAvatarUrl(user)} alt={user.name} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    user?.name?.charAt(0)
                  )}
                </div>
                <span className={`text-sm font-medium hidden lg:inline ${isDashboard ? 'text-white/90' : 'text-slate-700'}`}>
                  {user?.name?.split(' ')[0]}
                </span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header with Logo */}
      <header className={`z-50 sm:hidden ${
        isDashboard
          ? 'absolute top-0 left-0 right-0 bg-transparent'
          : 'sticky top-0 backdrop-blur-xl bg-white/90 border-b border-slate-100/50'
      }`}>
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className={`${isDashboard ? 'w-12 h-12 rounded-full ring-2 ring-white/30 shadow-lg' : 'h-14 w-auto'} overflow-hidden flex-shrink-0`}>
              <img 
                src="/logo-header.png" 
                alt="Ananda Sport" 
                className={`${isDashboard ? 'w-full h-full object-cover rounded-full' : 'h-14 w-auto'}`}
              />
            </div>
            <span className={`font-outfit text-xl font-bold whitespace-nowrap ${isDashboard ? 'bg-gradient-to-r from-green-300 via-white to-red-300 bg-clip-text text-transparent drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]' : 'text-slate-900'}`}>
              Ananda Sporting Club
            </span>
          </Link>
          <Link to="/profile" className="w-10 h-10 bg-sky-400 rounded-full flex items-center justify-center text-white text-sm font-medium overflow-hidden">
            {getAvatarUrl(user) ? (
              <img src={getAvatarUrl(user)} alt={user.name} className="w-full h-full rounded-full object-cover" />
            ) : (
              user?.name?.charAt(0)
            )}
          </Link>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 sm:hidden pb-safe" style={{ paddingBottom: 'env(safe-area-inset-bottom, 8px)', zIndex: 10000 }}>
        <div className="flex justify-around py-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const showBadge = item.path === '/chat' && unreadCount > 0;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`mobile-nav-${item.path.slice(1)}`}
                className={`relative flex flex-col items-center py-1 px-3 rounded-xl transition-colors ${
                  isActive(item.path)
                    ? 'text-sky-500'
                    : 'text-slate-400'
                }`}
              >
                <Icon className="w-6 h-6" />
                <span className="text-xs mt-1">{item.label}</span>
                {showBadge && (
                  <span className="absolute -top-1 right-0 w-5 h-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default Header;
